import { LoaderCircle } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";

interface IColors {
  [key: string]: string;
}

interface ColorSchemaPreviewProps {
  colors: IColors;
  baseUrl: string;
}

const LOADING_TIMEOUT_MS = 15000;

const ColorSchemaPreviewComponent: React.FC<ColorSchemaPreviewProps> = ({
  colors,
  baseUrl,
}) => {
  const [iframeUrl, setIframeUrl] = useState<string>(baseUrl);
  const [urlError, setUrlError] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);

  // Remember the last colors/baseUrl that produced a reload. When the parent
  // re-renders without an actual change, we skip the URL rebuild and iframe
  // navigation entirely. This prevents 25 iframes from reloading on Reset
  // when most schemes already hold the default colors, and removes the
  // "loader flicker" on unrelated parent re-renders.
  const lastColorsKeyRef = useRef<string>("");
  const lastBaseUrlRef = useRef<string>(baseUrl);

  const isValidUrl = (urlString: string): boolean => {
    try {
      new URL(urlString);
      return true;
    } catch (e) {
      return false;
    }
  };

  useEffect(() => {
    const colorsKey = JSON.stringify(colors);
    if (
      colorsKey === lastColorsKeyRef.current &&
      baseUrl === lastBaseUrlRef.current
    ) {
      return; // No actual change → no reload, no flicker
    }
    lastColorsKeyRef.current = colorsKey;
    lastBaseUrlRef.current = baseUrl;

    if (!isValidUrl(baseUrl)) {
      setUrlError("Invalid URL");
      setIsLoading(false);
      return;
    }

    setUrlError("");
    setIsLoading(true);

    const url = new URL(baseUrl);
    const params = new URLSearchParams();
    Object.entries(colors).forEach(([key, value]) => {
      params.append(key, encodeURIComponent(value));
    });
    url.search = params.toString();
    setIframeUrl(url.toString());
  }, [colors, baseUrl]);

  // Safety net: if the iframe never fires onLoad within the timeout
  // (e.g. server slow / too many concurrent requests on Reset), hide the
  // loader so the user is not stuck on a spinner indefinitely.
  useEffect(() => {
    if (!isLoading) return;
    const timer = setTimeout(() => setIsLoading(false), LOADING_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [isLoading, iframeUrl]);

  return (
    <div
      className={
        "flex flex-col gap-2 items-center justify-center w-full h-full relative min-h-[420px]"
      }
    >
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/90 z-20">
          <div className="flex flex-col items-center gap-2">
            <LoaderCircle className="w-8 h-8 text-primary animate-spin" />
          </div>
        </div>
      )}
      {urlError && <p style={{ color: "red" }}>{urlError}</p>}
      <iframe
        src={iframeUrl}
        loading="lazy"
        width="100%"
        height="100%"
        title="Color Preview"
        allow="fullscreen"
        onLoad={() => setIsLoading(false)}
        className={isLoading ? "invisible" : "visible"}
        style={{
          zoom: "80%",
          background: "#FFFFFF",
          position: "absolute",
          top: "0",
          left: "0",
          bottom: "0",
          right: "0",
          zIndex: 0,
        }}
      />
    </div>
  );
};

const ColorSchemaPreview = React.memo(ColorSchemaPreviewComponent);

export default ColorSchemaPreview;
